import { useState, useCallback } from 'react'

export interface GlobalAnnouncement {
  id: string
  type: 'success' | 'info' | 'warning'
  title: string
  message: string
  timestamp: string
}

const STORAGE_KEY = 'gracklecrm_dismissed_announcements'

/**
 * Hook to manage global announcements that persist until individually dismissed.
 * Dismissals are stored in localStorage so they persist across page refreshes.
 */
export function useGlobalAnnouncements() {
  const [announcements, setAnnouncements] = useState<GlobalAnnouncement[]>([])

  // Load dismissed IDs from localStorage
  const getDismissedIds = useCallback((): Set<string> => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY)
      return stored ? new Set(JSON.parse(stored)) : new Set()
    } catch {
      return new Set()
    }
  }, [])

  const addAnnouncement = useCallback(
    (announcement: GlobalAnnouncement) => {
      const dismissedIds = getDismissedIds()
      if (dismissedIds.has(announcement.id)) return // Already dismissed by this user

      setAnnouncements((prev) => {
        // Don't add duplicates
        if (prev.some((a) => a.id === announcement.id)) return prev
        return [announcement, ...prev]
      })
    },
    [getDismissedIds]
  )

  const dismissAnnouncement = useCallback(
    (id: string) => {
      setAnnouncements((prev) => prev.filter((a) => a.id !== id))

      // Persist dismissal in localStorage
      const dismissedIds = getDismissedIds()
      dismissedIds.add(id)
      localStorage.setItem(STORAGE_KEY, JSON.stringify([...dismissedIds]))
    },
    [getDismissedIds]
  )

  const clearAllAnnouncements = useCallback(() => {
    setAnnouncements([])
  }, [])

  return { announcements, addAnnouncement, dismissAnnouncement, clearAllAnnouncements }
}
